import { useState, useCallback, useRef, useEffect } from 'react';
import { searchNotes as searchNotesAPI } from '../services/notesService';

/**
 * useNoteSearch — Debounced search with instant results.
 *
 * @param {number} debounceMs — Debounce delay (default 400ms)
 * @returns {{ query, setQuery, results, isSearching, error, clearSearch }}
 */
export default function useNoteSearch(debounceMs = 400) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);

    const doSearch = useCallback(async (q) => {
        if (!q || q.trim().length < 2) {
            setResults([]);
            setIsSearching(false);
            return;
        }
        try {
            setIsSearching(true);
            setError(null);
            const res = await searchNotesAPI({ q: q.trim(), limit: 15 });
            const data = res?.data || res;
            setResults(data?.results || []);
        } catch (err) {
            console.error('[NoteSearch]', err);
            setError(err);
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    }, []);

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (!query || query.trim().length < 2) {
            setResults([]);
            return;
        }
        timerRef.current = setTimeout(() => doSearch(query), debounceMs);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [query, debounceMs, doSearch]);

    const clearSearch = useCallback(() => {
        setQuery('');
        setResults([]);
        setError(null);
    }, []);

    return { query, setQuery, results, isSearching, error, clearSearch };
}
